import LinkButton from 'components/atoms/LinkButton'
import TextClamp from 'components/atoms/TextClamp'
import Typography from 'components/atoms/_layouts/Typography'
import EditTable, { IColumn } from 'components/organisms/EditTable'
import ReservationCancelModalBody from '../../../organisms/Reservations/ReservationCancelModalBody'
import { ReservationIndexFilter } from '../parts/ReservationIndexFilter'
import moment from 'moment'
import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../../../core/i18n'
import { IJsonResponse } from '../../../../core/JsonApiSerializer'
import { reservationService, utilService } from '../../../../core/services'
import * as constants from '../../../../static/constants'
import * as interfaces from '../../../../static/interfaces'
import * as Hooks from '../../../../utils/hooks'
import { Button, Buttons, Pagination, Panel, StatusBadge } from '../../../atoms'
import SvgIcon from 'components/atoms/SvgIcon'

declare var window: interfaces.IWindow

interface IProps {
  reservations: {
    reservations: IJsonResponse
  }
  currentUser: any
  isMobile: boolean
}

const ReservationsIndex: React.FC<IProps> = props => {
  Hooks.SetDocumentTitle(I18n.t('top_nav.reservations'))
  const { reservations: initialReservations } = reservationService.getReservationsFromJson(
    props.reservations.reservations
  )

  const [reservations, setReservations] = React.useState(initialReservations)
  const [meta, setMeta] = React.useState(props.reservations.reservations.meta)

  const showCancelModal = reservation => {
    window.globalModal.showModal({
      title: I18n.t('cancel.cancel_reservation'),
      body: (
        <ReservationCancelModalBody
          reservationId={reservation.id}
          reservationStatus={reservation.workflow_state_enum_value}
        />
      ),
    })
  }

  const renderWorkFlowStateButtons = reservation => {
    const { id, next_work_flow_state } = reservation
    if (!next_work_flow_state) {
      return null
    }

    return next_work_flow_state.map((state, index) => {
      switch (state) {
        case 'canceled':
          return (
            <Button
              key={`${id}-${index}`}
              handleClick={() => showCancelModal(reservation)}
              textColor={constants.COLORS.caution}
              borderColor={constants.COLORS.caution}
              small={true}
              rounded={true}
            >
              {I18n.t('cancel.cancel_reservation')}
            </Button>
          )
        default:
          return null
      }
    })
  }

  const WorkFlowButtons: React.FC<{
    reservation: any
  }> = ({ reservation }) => {
    const conversationId = reservation.conversation?.slug || reservation.conversation?.id
    return (
      <Buttons inTable={true} justifyContent="space-between">
        <LinkButton href={`/conversations/${conversationId}`} miniLink={true} showBorder={true}>
          {I18n.t('generic.message')}
        </LinkButton>
        {reservation.is_past
          ? reservation.reviewable && (
              <LinkButton href={`/reservations/${reservation.id}/reviews/new`} showBorder={true}>
                {I18n.t('review.write_review')}
              </LinkButton>
            )
          : renderWorkFlowStateButtons(reservation)}
        <LinkButton href={`/reservations/${reservation.slug}`} miniLink={true} showBorder={true}>
          {I18n.t('generic.show_detail')}
        </LinkButton>
      </Buttons>
    )
  }

  const onChangePageHandler = async page => {
    const currentUrlParams = new URLSearchParams(window.location.search)
    currentUrlParams.set('page', page)
    location.href = `reservations?${currentUrlParams.toString()}`
  }

  const tableRef = React.useRef(null)
  const scrollerRef = React.useRef(null)

  React.useEffect(() => {
    if (tableRef.current && scrollerRef.current) {
      const tableWidth = tableRef.current.offsetWidth
      scrollerRef.current.style.width = `${tableWidth}px`
    }
  }, [tableRef, scrollerRef])
  const getBadgeStateFromWorkflowState = ({ start_at, workflow_state_enum_value }) => {
    if (workflow_state_enum_value === 'requested') {
      return 'usually'
    }
    if (workflow_state_enum_value === 'approved') {
      return 'success'
    }
    if (workflow_state_enum_value === 'declined' || workflow_state_enum_value === 'canceled') {
      return 'info'
    }
    if (moment(start_at).format('YYYY-MM-DD') === moment().format('YYYY-MM-DD')) {
      return 'caution'
    }
    return 'empty'
  }
  const COLUMNS: IColumn[] = [
    {
      name: I18n.t('generic.status'),
      field: 'status',
      required: false,
      readOnly: true,
      renderElement: reservation => {
        const type = getBadgeStateFromWorkflowState(reservation)
        return <StatusBadge label={reservation.workflow_state} type={type} />
      },
    },
    {
      name: I18n.t('reservation.reservation_date'),
      field: 'created_at',
      required: false,
      readOnly: true,
      renderElement: reservation => (
        <>
          <Typography type="body" size="xs">
            {reservation.no}
          </Typography>
          <Typography type="body">{reservation.created_at}</Typography>
        </>
      ),
    },
    {
      name: I18n.t('post.post_name'),
      field: 'post_name',
      required: false,
      readOnly: true,
      renderElement: reservation => {
        return reservation.post ? (
          <LinkButton href={`/posts/${reservation.post.slug}`} alignLeft={true}>
            <Typography type="body" size="lg">
              <TextClamp line={2} width={260}>
                {reservation.post.name}
              </TextClamp>
            </Typography>
          </LinkButton>
        ) : null
      },
    },
    {
      name: I18n.t('generic.tour_schedule'),
      field: 'tour_schedule',
      required: false,
      readOnly: true,
      renderElement: reservation => (
        <div className="schedule_wrap">
          {reservation.start_at}-{reservation.end_at}
        </div>
      ),
    },
    {
      name: I18n.t('generic.price'),
      field: 'price',
      required: false,
      readOnly: true,
      renderElement: reservation => {
        if (reservation.workflow_state_enum_value === 'canceled') {
          const cancelledFee = utilService.formatPriceOnlyYen(reservation.guest_cancelled_fee ?? 0)
          return (
            <>{I18n.t('reservation.guest_cancelled_fee_with_price', { price: cancelledFee })}</>
          )
        }
        return <>{utilService.formatPriceOnlyYen(reservation.charge_price)}</>
      },
    },
    /*{
      name: I18n.t('cancel.cancel_reason'),
      field: 'cancel_reason',
      required: false,
      readOnly: true,
    },*/
  ]

  return (
    <ReservationsIndexWrapper>
      <Panel noDecoration={true}>
        <div className="ReservationIndex_Header">
          <h2>{I18n.t('generic.filter')}</h2>
          <ReservationIndexFilter currentPagePath={'reservations'} currentUserIsGuest={true} />
        </div>

        {reservations.length === 0 ? (
          <div className="ReservationIndex_NoReservation">
            <SvgIcon name="calendar_today" className="icon" ariaHidden={true} role="presentation" />
            <h1>{I18n.t('reservation.no_reservations')}</h1>
          </div>
        ) : (
          <EditTable
            editable={true}
            columns={COLUMNS}
            records={reservations}
            pagination={null}
            renderRecordControls={reservaion => <WorkFlowButtons reservation={reservaion} />}
          />
        )}

        {meta && (
          <Pagination
            onChangePageHandler={onChangePageHandler}
            currentPage={meta.current_page}
            prevPage={meta.prev_page}
            nextPage={meta.next_page}
            totalPages={meta.total_pages}
            totalCount={meta.total_count}
          />
        )}
      </Panel>
    </ReservationsIndexWrapper>
  )
}

const ReservationsIndexWrapper = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
    padding: 20px 16px;
  }
  .ReservationIndex_NoReservation {
    display: flex;
    align-items: center;
    flex-direction: column;
    padding-top: 24px;
    > .icon {
      width: 32px;
      height: 32px;
    }
    > h1 {
      margin-top: 12px;
      font-weight: bold;
      font-size: 20px;
    }
  }
  .ReservationIndex_Header {
    display: flex;
    align-items: center;
    padding: 12px 0;
    @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
      flex-direction: column;
      align-items: flex-start;
      padding-top: 0;
    }
    > h2 {
      font-size: 16px;
      margin: 16px 16px 0 0;
      @media (max-width: ${constants.BREAKPOINT_TABLET_MOBILE}px) {
        margin: -12px 0 8px;
      }
    }
  }
  .ReservationIndex_Filters {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }
  .Button_Section {
    display: flex;
  }
  .direct_reservation_hidden {
    display: none;
  }
  .schedule_wrap {
    width: 120px;
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      width: fit-content;
    }
  }
`

export default ReservationsIndex
